import React, { useMemo, useState } from "react";
import {
  getLockedTabPaths,
  getTabLockState,
  saveLockedTabPaths,
} from "../utils/adminTabAccess";

const ADMIN_USERNAME = "jasimmustafa";
const ADMIN_PASSWORD = "jasimmustafa0391";

export default function VfsAdminControlPage() {
  const [credentials, setCredentials] = useState({ username: "", password: "" });
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [lockedPaths, setLockedPaths] = useState(() => getLockedTabPaths());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  const tabs = useMemo(
    () => getTabLockState().map((tab) => ({
      ...tab,
      locked: lockedPaths.includes(tab.path.toLowerCase()),
    })),
    [lockedPaths]
  );

  const lockedCount = tabs.filter((tab) => tab.locked).length;
  const unlockedCount = tabs.length - lockedCount;

  const handleLogin = (event) => {
    event.preventDefault();
    setLoading(true);
    setError("");
    setMessage("");

    try {
      if (
        credentials.username !== ADMIN_USERNAME ||
        credentials.password !== ADMIN_PASSWORD
      ) {
        throw new Error("Invalid admin credentials.");
      }

      setIsAuthorized(true);
      setLockedPaths(getLockedTabPaths());
      setMessage("Admin tab control unlocked.");
    } catch (loginError) {
      setIsAuthorized(false);
      setError(loginError.message || "Invalid admin credentials.");
    } finally {
      setLoading(false);
    }
  };

  const handleToggleTab = (path) => {
    setError("");
    setMessage("");

    const normalizedPath = path.toLowerCase();
    const nextLockedPaths = lockedPaths.includes(normalizedPath)
      ? lockedPaths.filter((lockedPath) => lockedPath !== normalizedPath)
      : [...lockedPaths, normalizedPath];

    if (nextLockedPaths.length === tabs.length) {
      setError("At least one sidebar tab must remain unlocked.");
      return;
    }

    saveLockedTabPaths(nextLockedPaths);
    setLockedPaths(nextLockedPaths);
    setMessage("Sidebar tab permissions updated.");
  };

  const handleUnlockAll = () => {
    saveLockedTabPaths([]);
    setLockedPaths([]);
    setMessage("All sidebar tabs unlocked.");
  };

  const handleLockAllExceptDashboard = () => {
    const nextLockedPaths = tabs
      .filter((tab) => tab.path.toLowerCase() !== "/dashboard")
      .map((tab) => tab.path.toLowerCase());

    saveLockedTabPaths(nextLockedPaths);
    setLockedPaths(nextLockedPaths);
    setMessage("All tabs except Dashboard have been locked.");
  };

  const handleLockPanel = () => {
    setIsAuthorized(false);
    setCredentials({ username: "", password: "" });
    setMessage("");
    setError("");
    setLockedPaths(getLockedTabPaths());
  };

  const getButtonClassName = (locked) => {
    return locked ? "btn btn-sm btn-success" : "btn btn-sm btn-outline-danger";
  };

  const getButtonLabel = (locked) => {
    return locked ? "Unlock" : "Lock";
  };

  const getStatusBadgeClassName = (locked) => {
    return locked ? "badge bg-danger-subtle text-danger" : "badge bg-success-subtle text-success";
  };

  const getStatusLabel = (locked) => {
    return locked ? "Locked" : "Unlocked";
  };

  if (!isAuthorized) {
    return (
      <div
        className="d-flex align-items-center justify-content-center px-3"
        style={{ minHeight: "100vh", background: "linear-gradient(135deg, #0f172a, #1e293b)" }}
      >
        <div className="card border-0 shadow-lg" style={{ width: "100%", maxWidth: "420px", borderRadius: "20px" }}>
          <div className="card-body p-4 p-md-5">
            <div className="mb-4">
              <div className="text-uppercase text-muted fw-semibold small mb-2">Restricted Route</div>
              <h2 className="fw-bold mb-2">Sidebar Admin Control</h2>
              <p className="text-muted mb-0">
                Authenticate to lock or unlock the sidebar tabs shown to logged-in users.
              </p>
            </div>

            {error && <div className="alert alert-danger py-2">{error}</div>}

            <form onSubmit={handleLogin}>
              <div className="mb-3">
                <label className="form-label fw-semibold">Username</label>
                <input
                  className="form-control form-control-lg"
                  value={credentials.username}
                  onChange={(event) =>
                    setCredentials((prev) => ({ ...prev, username: event.target.value }))
                  }
                  autoComplete="username"
                />
              </div>
              <div className="mb-4">
                <label className="form-label fw-semibold">Password</label>
                <input
                  type="password"
                  className="form-control form-control-lg"
                  value={credentials.password}
                  onChange={(event) =>
                    setCredentials((prev) => ({ ...prev, password: event.target.value }))
                  }
                  autoComplete="current-password"
                />
              </div>
              <button className="btn btn-dark btn-lg w-100" type="submit" disabled={loading}>
                {loading ? "Checking..." : "Enter Admin Panel"}
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", background: "#f8fafc" }}>
      <div className="container py-4 py-md-5">
        <div className="d-flex justify-content-between align-items-start flex-wrap gap-3 mb-4">
          <div>
            <div className="text-uppercase text-muted fw-semibold small mb-2">Secret Route</div>
            <h1 className="fw-bold mb-2">Sidebar Lock Admin Panel</h1>
            <p className="text-muted mb-0" style={{ maxWidth: "760px" }}>
              Use this panel to lock or unlock frontend sidebar tabs. Locked tabs disappear from the sidebar and direct URL access is redirected to the first unlocked tab.
            </p>
          </div>
          <button className="btn btn-outline-secondary" onClick={handleLockPanel}>
            Lock Panel
          </button>
        </div>

        {(error || message) && (
          <div className={`alert ${error ? "alert-danger" : "alert-success"} py-2`}>
            {error || message}
          </div>
        )}

        <div className="row g-4 mb-4">
          <div className="col-md-4">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-body">
                <div className="text-uppercase text-muted fw-semibold small mb-2">Unlocked Tabs</div>
                <div className="fs-4 fw-bold">{unlockedCount}</div>
                <div className="text-muted small mt-2">Tabs currently available in the sidebar.</div>
              </div>
            </div>
          </div>
          <div className="col-md-4">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-body">
                <div className="text-uppercase text-muted fw-semibold small mb-2">Locked Tabs</div>
                <div className="fs-4 fw-bold">{lockedCount}</div>
                <div className="text-muted small mt-2">Tabs removed from the sidebar and blocked by route guard.</div>
              </div>
            </div>
          </div>
          <div className="col-md-4">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-body">
                <div className="text-uppercase text-muted fw-semibold small mb-2">Authority</div>
                <div className="fw-semibold">Admin username: {ADMIN_USERNAME}</div>
                <div className="fw-semibold">Managed tabs: {tabs.length}</div>
                <div className="text-muted small mt-2">Changes apply immediately in this browser.</div>
              </div>
            </div>
          </div>
        </div>

        <div className="row g-4">
          <div className="col-lg-7">
            <div className="card border-0 shadow-sm">
              <div className="card-body p-4">
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                  <h5 className="fw-bold mb-0">Sidebar Permissions</h5>
                  <div className="d-flex gap-2 flex-wrap">
                    <button className="btn btn-sm btn-outline-secondary" onClick={handleUnlockAll}>
                      Unlock All
                    </button>
                    <button className="btn btn-sm btn-dark" onClick={handleLockAllExceptDashboard}>
                      Lock All Except Dashboard
                    </button>
                  </div>
                </div>

                <div className="list-group list-group-flush">
                  {tabs.map((tab) => (
                    <div
                      key={tab.path}
                      className="list-group-item d-flex justify-content-between align-items-center px-0 py-3"
                    >
                      <div>
                        <div className="fw-semibold">{tab.name}</div>
                        <div className="text-muted small">{tab.path}</div>
                      </div>
                      <div className="d-flex align-items-center gap-2">
                        <span className={getStatusBadgeClassName(tab.locked)}>{getStatusLabel(tab.locked)}</span>
                        <button
                          className={getButtonClassName(tab.locked)}
                          onClick={() => handleToggleTab(tab.path)}
                        >
                          {getButtonLabel(tab.locked)}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="col-lg-5">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-body p-4">
                <h5 className="fw-bold mb-3">Behavior Notes</h5>
                <div className="text-muted small mb-3">
                  Locked tabs are removed from the sidebar immediately for this browser profile.
                </div>
                <div className="text-muted small mb-3">
                  If someone types a locked route directly in the URL, the app redirects them to the first unlocked tab.
                </div>
                <div className="text-muted small">
                  Extension control changes have been removed from this admin panel. This route now manages frontend navigation access only.
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}